import { ArrowUpRight, Clock } from "lucide-react";

const GOLD = "#B89968";
const GOLD_SOFT = "#D8C39A";
const INK = "#161618";
const IVORY = "#F1EDDE";

const TREATMENTS = [
  {
    id: "anti-wrinkle",
    name: "Anti-Wrinkle",
    duration: "30 min",
    desc: "Soften expression lines across the forehead, glabella and crow's feet with precise, micro-dose placement.",
    img: "https://customer-assets.emergentagent.com/job_beauty-templates/artifacts/woafsq3c_802a989d-201d-4ab0-9075-889bcbea84d7.jpeg",
  },
  {
    id: "lip-augmentation",
    name: "Lip Augmentation",
    duration: "45 min",
    desc: "Hydrated, elegantly shaped lips. Russian, Paris or natural hydration techniques.",
    img: "https://customer-assets.emergentagent.com/job_beauty-templates/artifacts/4p52fwub_e05e20ca-ed17-4921-9e84-b6720f6a9411.jpeg",
  },
  {
    id: "dermal-filler",
    name: "Dermal Filler",
    duration: "45 min",
    desc: "Cheeks, jawline, chin, tear trough — structural restoration with hyaluronic acid.",
    img: "https://customer-assets.emergentagent.com/job_beauty-templates/artifacts/d15go17y_8d4e95f7-3551-4722-b96b-252a1418848d.jpeg",
  },
  {
    id: "injectable-skincare",
    name: "Injectable Skincare",
    duration: "30 min",
    desc: "Injectable skincare is ideal for dull, dehydrated, or ageing skin and is always tailored with a natural, skin-health-first approach in mind.",
    img: "https://customer-assets.emergentagent.com/job_beauty-templates/artifacts/fr8r69pd_0333df21-e7a4-40ec-9524-d3967e4b9485.jpeg",
  },
  {
    id: "anti-aging",
    name: "Anti-Aging Treatments",
    duration: "45 min",
    desc: "My anti-ageing treatments are designed to restore skin quality, support collagen production and soften the visible signs of ageing. From collagen-stimulating treatments like Sculptra and microneedling to regenerative therapies such as PRP.",
    img: "https://customer-assets.emergentagent.com/job_beauty-templates/artifacts/4643uknm_d35701e3-c033-49dd-b049-f0ba948aba49.jpeg",
  },
  {
    id: "laser-ipl",
    name: "Laser + IPL",
    duration: "60 min",
    desc: "Laser tattoo removal, IPL hair reduction, carbon facial, thread vein removal and CO₂ resurfacing.",
    img: "https://customer-assets.emergentagent.com/job_beauty-templates/artifacts/4crixz9s_3ae436c8-5af0-4313-a160-c9eb63842e2c.jpeg",
  },
];

export const Treatments = ({ onBook }) => {
  return (
    <section
      id="treatments"
      data-testid="treatments-section"
      className="relative py-16 lg:py-24 overflow-hidden"
      style={{ background: IVORY }}
    >
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
        {/* Header */}
        <div className="text-center mb-10 reveal">
          <div
            className="text-[0.6rem] uppercase tracking-[0.32em] mb-3"
            style={{ color: GOLD }}
          >
            Stacy Fox · Laser &amp; Aesthetics
          </div>
          <h2
            className="font-serif font-light leading-[0.95]"
            style={{
              color: INK,
              fontSize: "clamp(2.5rem, 6vw, 4.75rem)",
              letterSpacing: "0.02em",
            }}
          >
            Treatment Menu
          </h2>
          <div
            className="mt-5 font-serif italic"
            style={{ color: GOLD, fontSize: "clamp(1.25rem, 2.4vw, 1.85rem)" }}
          >
            Refined results, tailored to your face.
          </div>
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className="h-px w-12" style={{ background: GOLD }} />
            <span className="text-xs" style={{ color: GOLD }}>♥</span>
            <span className="h-px w-12" style={{ background: GOLD }} />
          </div>
          <p
            className="mt-7 text-[0.72rem] sm:text-sm uppercase tracking-[0.28em] leading-relaxed"
            style={{ color: INK }}
          >
            Anatomy led · Natural results · Long-term skin health
          </p>
        </div>

        {/* Treatment cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 lg:gap-8">
          {TREATMENTS.map((t, idx) => (
            <article
              key={t.id}
              data-testid={`treatment-card-${t.id}`}
              className="group relative bg-white shadow-md hover:-translate-y-2 hover:shadow-2xl transition-all duration-500 flex flex-col"
              style={{ border: `1px solid rgba(184,153,104,0.35)` }}
            >
              {/* Black header */}
              <div
                className="px-3 sm:px-6 pt-5 sm:pt-8 pb-4 sm:pb-6 text-center"
                style={{
                  background: INK,
                  color: IVORY,
                  clipPath:
                    "polygon(0 0, 100% 0, 100% calc(100% - 18px), 50% 100%, 0 calc(100% - 18px))",
                }}
              >
                <div
                  className="text-[0.5rem] sm:text-[0.55rem] uppercase tracking-[0.3em] mb-1 sm:mb-2"
                  style={{ color: GOLD_SOFT }}
                >
                  Treatment 0{idx + 1}
                </div>
                <h3 className="font-serif text-lg sm:text-3xl sm:text-[2rem] tracking-wide leading-tight">
                  {t.name}
                </h3>
              </div>

              {/* Image */}
              <div className="aspect-[5/4] overflow-hidden mt-2 mx-2">
                <img
                  src={t.img}
                  alt={t.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              {/* Body */}
              <div className="px-3 sm:px-7 pt-4 sm:pt-7 pb-4 sm:pb-6 text-center flex-1 flex flex-col">
                <div
                  className="inline-flex items-center justify-center gap-2 self-center text-[0.5rem] sm:text-[0.6rem] uppercase tracking-[0.22em] sm:tracking-[0.28em] mb-3 sm:mb-5"
                  style={{ color: GOLD }}
                >
                  <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={1.5} />
                  {t.duration}
                </div>
                <p
                  className="text-[0.72rem] sm:text-[0.95rem] leading-relaxed flex-1"
                  style={{ color: INK }}
                >
                  {t.desc}
                </p>
                <a
                  href="#price-list"
                  data-testid={`treatment-pricing-${t.id}`}
                  className="mt-4 sm:mt-6 inline-flex items-center justify-center gap-2 text-[0.55rem] sm:text-[0.65rem] uppercase tracking-[0.22em] sm:tracking-[0.28em] self-center"
                  style={{ color: GOLD }}
                >
                  View pricing
                  <ArrowUpRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                </a>
              </div>

              {/* CTA */}
              <button
                onClick={() => onBook?.(t.name)}
                data-testid={`treatment-book-${t.id}`}
                className="block w-full py-3 sm:py-4 text-[0.55rem] sm:text-[0.7rem] uppercase tracking-[0.22em] sm:tracking-[0.28em] font-medium transition-colors"
                style={{
                  background: INK,
                  color: IVORY,
                  borderTop: `1px solid ${GOLD}`,
                }}
              >
                Book {t.name}
              </button>

              <div className="h-1 w-full" style={{ background: GOLD }} />
            </article>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-16 text-center reveal">
          <div className="flex items-center justify-center gap-3">
            <span className="h-px w-12" style={{ background: GOLD }} />
            <span className="text-xs" style={{ color: GOLD }}>♥</span>
            <span className="h-px w-12" style={{ background: GOLD }} />
          </div>
          <p
            className="mt-6 font-serif italic"
            style={{ color: INK, fontSize: "1.1rem" }}
          >
            Every treatment begins with a thorough consultation —
            <br className="hidden sm:block" /> no pressure, just honest advice.
          </p>
          <p
            className="mt-3 text-[0.62rem] uppercase tracking-[0.3em]"
            style={{ color: "#8C8580" }}
          >
            Full prices in the brochure below
          </p>
        </div>
      </div>
    </section>
  );
};

export default Treatments;
